import React, { useState, useEffect, useCallback } from 'react';
import AuthGuard from './AuthGuard';
import {
  Admin,
  Resource,
  Layout,
  Sidebar,
  Menu,
  MenuItem,
  useDataProvider,
  useGetList,
  useNotify
} from 'react-admin';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Bot,
  MessageSquare,
  Share2,
  BookOpen,
  Brain,
  Globe,
  TrendingUp,
  ThumbsUp,
  Code,
  BarChart3,
  Settings,
  Plus,
  Menu as MenuIcon,
  X,
  Clock,
  User,
  ChevronDown,
  Search,
  MessageCircle,
  ChevronRight,
  Star,
  History,
  MoreHorizontal
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ContextUpload from './ContextUpload';
import ChatInterface from './ChatInterface';
import EmbedAgent from './EmbedAgent';
import ConversationHistory from './ConversationHistory';
import AgentSettings from './AgentSettings';
import WebsiteScanner from './WebsiteScanner';
import TuneYourAgent from './TuneYourAgent';
import ImproveAgent from './ImproveAgent';
import UserDropdown from './UserDropdown';
import PublicChatInterface from './PublicChatInterface';
import './ReactAdminPrototype.css';

// Custom Layout Component
const CustomLayout = (props) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Determine mode based on current route
  const mode = location.pathname.startsWith('/talkto') ? 'discover' : 'build';
  
  const [recentConversations, setRecentConversations] = useState([]);
  const [showAllConversations, setShowAllConversations] = useState(false);
  const [expandedSubsections, setExpandedSubsections] = useState(['recent-conversations', 'my-chats']);
  const [recentChats, setRecentChats] = useState([]);
  const [showAllChats, setShowAllChats] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isInChat, setIsInChat] = useState(false);
  const [notifications, setNotifications] = useState(0);

  // React Admin expects children to be rendered
  const { children } = props;

  // Check for owner responses in conversations
  const checkForOwnerResponses = useCallback(async (conversations) => {
    try {
      // For now, set notifications to 0 since we don't have a read/unread system yet
      setNotifications(0);
    } catch (error) {
      console.error('Error checking for owner responses:', error);
    }
  }, []);

  // Load recent chats for Discover mode (conversations with other agents)
  const loadRecentChats = async () => {
    try {
      // Check if we should use mock data (for screenshots/demos)
      const useMockData = window.location.search.includes('mock=true') || 
                         (window.location.hostname === 'localhost' && 
                          localStorage.getItem('useMockAgents') === 'true');
      
      console.log('ReactAdminPrototype mock data detection:', {
        search: window.location.search,
        hostname: window.location.hostname,
        localStorage: localStorage.getItem('useMockAgents'),
        useMockData: useMockData,
        isAuthenticated: isAuthenticated
      });
      
      // If using mock data, load it regardless of authentication status
      if (useMockData) {
        const mockParam = '?mock=true';
        console.log('Loading mock conversations from ReactAdminPrototype:', `/api/agents/public/my-conversations${mockParam}`);
        const response = await fetch(`/api/agents/public/my-conversations${mockParam}`);
        console.log('Mock conversations response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Loaded mock conversations:', data.conversations?.length || 0);
          console.log('Mock conversation titles:', data.conversations?.map(c => c.title));
          setRecentChats(data.conversations || []);
          checkForOwnerResponses(data.conversations || []);
          return;
        } else {
          console.error('Failed to load mock conversations, status:', response.status);
        }
      }
      
      // Only load real conversations if user is authenticated
      if (!isAuthenticated) {
        setRecentChats([]);
        return;
      }
      
      const response = await fetch('/api/agents/public/my-conversations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentChats(data.conversations || []);
        checkForOwnerResponses(data.conversations || []);
      } else {
        console.error('Failed to load user chats:', response.status);
        setRecentChats([]);
      }
    } catch (error) {
      console.error('Error loading user chats:', error);
      setRecentChats([]);
    }
  };


  // Handle URL parameters for chat navigation
  useEffect(() => {
    const chatId = searchParams.get('chat');
    const agentId = searchParams.get('agent');
    
    console.log('URL params changed:', { chatId, agentId, recentChats: recentChats.length });
    
    if (chatId) {
      // If we have a chatId in URL, try to find the chat
      const chat = recentChats.find(c => c.id.toString() === chatId);
      console.log('Found chat:', chat);
      
      if (chat) {
        // Chat found, load it
        console.log('Loading chat from URL:', chat);
        setSelectedConversationId(chatId);
        const agentData = {
          id: chat.agent_id,
          name: chat.agent_name,
          agent_avatar: chat.agent_avatar,
          creator_name: chat.creator_name,
          creator_picture: chat.creator_picture
        };
        setSelectedAgent(agentData);
        setIsInChat(true);
      } else if (recentChats.length > 0) {
        // Chat not found but we have loaded chats, clear the URL
        console.log('Chat not found, clearing URL parameters');
        setSearchParams({});
      }
      // If recentChats is still loading (length === 0), wait for it to load
    } else if (agentId) {
      // If we have an agentId but no chatId, start a new chat with that agent
      console.log('Starting new chat with agent ID:', agentId);
      // We need to fetch the agent data from the public agents list
      fetch(`/api/agents/public`)
        .then(response => response.json())
        .then(data => {
          const agent = data.agents?.find(a => a.id === parseInt(agentId));
          if (agent) {
            console.log('Found agent for new chat:', agent);
            setSelectedAgent(agent);
            setSelectedConversationId(null);
            setIsInChat(true);
          } else {
            console.log('Agent not found, clearing URL parameters');
            setSearchParams({});
          }
        })
        .catch(error => {
          console.error('Error fetching agent data:', error);
          setSearchParams({});
        });
    } else {
      // No chat or agent in URL, exit chat mode
      setIsInChat(false);
      setSelectedAgent(null);
      setSelectedConversationId(null);
    }
  }, [searchParams, recentChats, setSearchParams]);

  // Listen for agent chat events
  useEffect(() => {
    const handleStartAgentChat = (event) => {
      const agent = event.detail.agent;
      console.log('Starting chat with agent:', agent);
      setSelectedAgent(agent);
      setSelectedConversationId(null); // Start new conversation
      setIsInChat(true);
      // Update URL to indicate we're starting a new chat with this agent
      setSearchParams({ agent: agent.id.toString() });
    };

    const handleConversationCreated = () => {
      // Refresh conversations when a new one is created
      loadRecentChats();
    };

    const handleUpdateChatUrl = (event) => {
      const conversationId = event.detail.conversationId;
      console.log('Updating URL with conversation ID:', conversationId);
      setSearchParams({ chat: conversationId.toString() });
    };

    window.addEventListener('startAgentChat', handleStartAgentChat);
    window.addEventListener('conversationCreated', handleConversationCreated);
    window.addEventListener('updateChatUrl', handleUpdateChatUrl);
    
    return () => {
      window.removeEventListener('startAgentChat', handleStartAgentChat);
      window.removeEventListener('conversationCreated', handleConversationCreated);
      window.removeEventListener('updateChatUrl', handleUpdateChatUrl);
    };
  }, [loadRecentChats, setSearchParams]);

  // Load recent conversations
  const loadRecentConversations = async () => {
    if (!user?.agent?.id) return;
    
    try {
      const response = await fetch(`/api/agents/${user.agent.id}/conversations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only show owner conversations (agent owner chatting with their own agent)
        // Owner conversations are: is_embedded = false AND user_id IS NULL
        const ownerConversations = (data.conversations || []).filter(conv => 
          !conv.is_embedded && !conv.user_id
        );
        setRecentConversations(ownerConversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  // Allow unauthenticated users to view the interface
  // Individual interactive elements will be wrapped with AuthGuard

  useEffect(() => {
    if (user?.agent?.id) {
      loadRecentConversations();
    }
  }, [user?.agent?.id]);

  useEffect(() => {
    if (user) {
      loadRecentChats();
    }
  }, [user]);

  // Load chats when switching to Discover mode
  useEffect(() => {
    if (mode === 'discover' && user) {
      loadRecentChats();
    }
  }, [mode, user]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <Brain size={64} />
        <div>Loading...</div>
      </div>
    );
  }

  // Allow unauthenticated users to view the interface
  // Individual interactive elements will be wrapped with AuthGuard

  // Toggle subsection expansion
  const toggleSubsection = (subsectionId) => {
    setExpandedSubsections(prev => 
      prev.includes(subsectionId) 
        ? prev.filter(id => id !== subsectionId)
        : [...prev, subsectionId]
    );
  };

  // Handle conversation click
  const handleConversationClick = (conversationId) => {
    navigate(`/build/c/${conversationId}`);
  };

  // Handle chat click (for Discover mode)
  const handleChatClick = (chat) => {
    console.log('Chat clicked:', chat);
    // Set chat state directly for immediate response
    setSelectedConversationId(chat.id);
    const agentData = {
      id: chat.agent_id,
      name: chat.agent_name,
      agent_avatar: chat.agent_avatar,
      creator_name: chat.creator_name,
      creator_picture: chat.creator_picture
    };
    setSelectedAgent(agentData);
    setIsInChat(true);
    
    // Also update URL parameters
    setSearchParams({ chat: chat.id.toString() });
  };

  const handleBackFromChat = () => {
    // Clear URL parameters to go back to discover page
    setSearchParams({});
    console.log('Back from chat - navigating to discover');
  };

  const handleUpdateUrl = (params) => {
    console.log('ReactAdminPrototype: Updating URL with params:', params);
    setSearchParams(params);
  };

  
  
  return (
    <div className="react-admin-prototype" data-theme="dark">
      {/* App Header - Match /build exactly */}
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src={process.env.PUBLIC_URL + "/brain-logo.png"} 
              alt="Brain Logo" 
              className="title-icon"
            />
            Agent You
          </h1>
          <p className="app-subtitle">Don't just use a tool, be a tool!</p>
          <div className={`mode-toggle ${mode === 'discover' ? 'discover-mode' : ''}`}>
            <AuthGuard>
              <button 
                className={`toggle-button ${mode === 'build' ? 'active' : ''}`}
                onClick={() => navigate('/build')}
              >
                Build
              </button>
            </AuthGuard>
            <button 
              className={`toggle-button ${mode === 'discover' ? 'active' : ''}`}
              onClick={() => navigate('/talkto')}
            >
              Discover
            </button>
          </div>

          <AuthGuard 
            fallback={
              <div className="user-section">
                <button 
                  className="signup-button"
                  onClick={() => {
                    const isDevelopment = window.location.hostname === 'localhost';
                    const oauthUrl = isDevelopment 
                      ? 'https://localhost:5001/auth/google?return_to=/talkto' 
                      : '/auth/google?return_to=/talkto';
                    window.location.href = oauthUrl;
                  }}
                >
                  Sign Up
                </button>
              </div>
            }
          >
            <div className="user-section">
              <div className="user-info" onClick={() => setShowUserDropdown(!showUserDropdown)}>
                <span className="user-name">{user?.name || 'User'}</span>
                <span className="user-email">{user?.email || 'user@example.com'}</span>
              </div>
              <div className="user-avatar" onClick={() => setShowUserDropdown(!showUserDropdown)}>
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} />
                ) : (
                  <User className="default-avatar" />
                )}
              </div>
              {showUserDropdown && (
                <UserDropdown 
                  user={user} 
                  onClose={() => setShowUserDropdown(false)} 
                />
              )}
            </div>
          </AuthGuard>
        </div>
        
        {/* Mobile Menu Button - Only visible on mobile */}
        <button 
          className="mobile-menu-button"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>
        
        {/* Mobile Toggle - Only visible on mobile */}
        <div className="mobile-mode-toggle">
          <AuthGuard>
            <button 
              className={`mobile-toggle-button ${mode === 'build' ? 'active' : ''}`}
              onClick={() => navigate('/build')}
              title="Build Mode"
            >
              <Bot size={16} />
            </button>
          </AuthGuard>
          <button 
            className={`mobile-toggle-button ${mode === 'discover' ? 'active' : ''}`}
            onClick={() => navigate('/talkto')}
            title="Discover Mode"
          >
            <Search size={16} />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <nav className="sidebar-nav">
          {/* Build Navigation */}
          {mode === 'build' && (
            <>
              {!isAuthenticated ? (
                <div className="nav-section">
                  <div className="section-header">
                    <Bot size={20} />
                    Build Your Agent
                  </div>
                  <div className="section-items">
                    <div className="nav-item-disabled">
                      <BookOpen size={20} />
                      <div className="item-content">
                        <div className="item-title">Sign up to build your agent</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="nav-section">
                    <button className="section-header">
                      <Bot size={20} />
                      Build Your Agent
                    </button>
                    <div className="section-items">
                      <button className="nav-item" onClick={() => navigate('/build/upload')}>
                        <BookOpen size={20} />
                        <div className="item-content">
                          <div className="item-title">Upload Files</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/text')}>
                        <Brain size={20} />
                        <div className="item-content">
                          <div className="item-title">Add Text</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/websites')}>
                        <Globe size={20} />
                        <div className="item-content">
                          <div className="item-title">Scan Websites</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/tune')}>
                        <TrendingUp size={20} />
                        <div className="item-content">
                          <div className="item-title">Answer Agent Questions</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/improve')}>
                        <ThumbsUp size={20} />
                        <div className="item-content">
                          <div className="item-title">Manage Agent Feedback</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="nav-section">
                    <button className="section-header">
                      <MessageSquare size={20} />
                      Chat with Your Agent
                    </button>
                    <div className="section-items">
                      <button className="nav-item" onClick={() => navigate('/build')}>
                        <Plus size={20} />
                        <div className="item-content">
                          <div className="item-title">New Chat</div>
                        </div>
                      </button>
                      
                      {/* Recent Conversations Subsection */}
                      {recentConversations.length > 0 && (
                        <div className="conversations-subsection">
                          <button
                            className="subsection-header"
                            onClick={() => toggleSubsection('recent-conversations')}
                          >
                            <History size={14} />
                            <span>Recent Conversations</span>
                            {expandedSubsections.includes('recent-conversations') ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </button>
                          {expandedSubsections.includes('recent-conversations') && (
                            <div className="conversations-list">
                              {(showAllConversations ? recentConversations : recentConversations.slice(0, 5)).map((conv) => (
                                <button
                                  key={conv.id}
                                  className="nav-item"
                                  onClick={() => handleConversationClick(conv.id)}
                                >
                                  <MessageSquare size={16} />
                                  <div className="item-content">
                                    <span className="item-title">
                                      {conv.title && conv.title.trim() ? 
                                        (conv.title.length > 50 ? 
                                          conv.title.substring(0, 50) + '...' : 
                                          conv.title
                                        ) : 
                                        `New Conversation ${conv.id}`
                                      }
                                    </span>
                                  </div>
                                </button>
                              ))}
                              {recentConversations.length > 5 && (
                                <button
                                  className="nav-item"
                                  onClick={() => setShowAllConversations(!showAllConversations)}
                                >
                                  <MoreHorizontal size={16} />
                                  <div className="item-content">
                                    <span className="item-title">
                                      {showAllConversations ? 'Show Less' : `Show All (${recentConversations.length})`}
                                    </span>
                                  </div>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="nav-section">
                    <button className="section-header">
                      <Share2 size={20} />
                      Share & Manage
                    </button>
                    <div className="section-items">
                      <button className="nav-item" onClick={() => navigate('/build/embed')}>
                        <Code size={20} />
                        <div className="item-content">
                          <div className="item-title">Share Your Agent</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/conversations')}>
                        <BarChart3 size={20} />
                        <div className="item-content">
                          <div className="item-title">View Conversations</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/agentsettings')}>
                        <Settings size={20} />
                        <div className="item-content">
                          <div className="item-title">Agent Settings</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Discover Navigation */}
          {mode === 'discover' && (
            <>
              <div className="nav-section">
                <button className="section-header direct-nav" onClick={() => navigate('/talkto')}>
                  <Search size={20} />
                  Discover Agents
                </button>
              </div>

              <div className="nav-section">
                <AuthGuard>
                  <button className="section-header" onClick={() => toggleSubsection('my-chats')}>
                    <MessageCircle size={20} />
                    My Chats
                    {expandedSubsections.includes('my-chats') ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                </AuthGuard>
                {expandedSubsections.includes('my-chats') && (
                  <div className="section-items">
                    {recentChats.length > 0 ? (
                      <>
                        {(showAllChats ? recentChats : recentChats.slice(0, 5)).map((chat) => (
                          <button
                            key={chat.id}
                            className="nav-item"
                            onClick={() => handleChatClick(chat)}
                          >
                            {chat.agent_avatar ? (
                              <img 
                                src={chat.agent_avatar} 
                                alt={chat.agent_name}
                                className="agent-avatar"
                                style={{ width: 32, height: 32, borderRadius: '50%' }}
                              />
                            ) : (
                              <MessageSquare size={16} />
                            )}
                            <div className="item-content">
                            <span className="item-title">
                              {chat.title && chat.title.trim() ? 
                                (chat.title.length > 50 ? 
                                  chat.title.substring(0, 50) + '...' : 
                                  chat.title
                                ) : 
                                `New Chat ${chat.id}`
                              }
                            </span>
                              <span className="item-subtitle">with {chat.agent_name} by {chat.creator_name}</span>
                            </div>
                          </button>
                        ))}
                        {recentChats.length > 5 && (
                          <button
                            className="nav-item"
                            onClick={() => setShowAllChats(!showAllChats)}
                          >
                            <MoreHorizontal size={16} />
                            <div className="item-content">
                              <span className="item-title">
                                {showAllChats ? 'Show Less' : `Show All (${recentChats.length})`}
                              </span>
                            </div>
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="nav-item">
                        <MessageCircle size={16} />
                        <div className="item-content">
                          <span className="item-title">No chats yet</span>
                          <span className="item-subtitle">Start chatting with agents</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="nav-section">
                <AuthGuard>
                  <button 
                    className="section-header direct-nav" 
                    onClick={() => {
                      // More explicit check for talkto route
                      if ((mode === 'discover' || location.pathname.startsWith('/talkto')) && isAuthenticated) {
                        // Navigate to talkto/settings for authenticated users on discover mode
                        navigate('/talkto/settings');
                      } else {
                        // Navigate to settings for build mode or unauthenticated users
                        navigate('/settings');
                      }
                    }}
                  >
                    <Settings size={20} />
                    Settings
                  </button>
                </AuthGuard>
              </div>
            </>
          )}
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {isInChat ? (
          <PublicChatInterface 
            key={selectedConversationId || 'new-chat'}
            agent={selectedAgent} 
            conversationId={selectedConversationId}
            onBack={handleBackFromChat}
            onUpdateUrl={handleUpdateUrl}
          />
        ) : (
          <div className="content-body">
            {React.Children.map(props.children, child => 
              React.isValidElement(child) ? React.cloneElement(child, { ...props }) : child
            )}
          </div>
        )}
      </div>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

// Simple Data Provider
const dataProvider = {
  getList: (resource, params) => {
    return Promise.resolve({ data: [], total: 0 });
  },
  getOne: (resource, params) => {
    return Promise.resolve({ data: { id: params.id } });
  },
  getMany: (resource, params) => {
    return Promise.resolve({ data: [] });
  },
  getManyReference: (resource, params) => {
    return Promise.resolve({ data: [], total: 0 });
  },
  create: (resource, params) => {
    return Promise.resolve({ data: { ...params.data, id: Date.now() } });
  },
  update: (resource, params) => {
    return Promise.resolve({ data: { ...params.data } });
  },
  updateMany: (resource, params) => {
    return Promise.resolve({ data: params.ids });
  },
  delete: (resource, params) => {
    return Promise.resolve({ data: { id: params.id } });
  },
  deleteMany: (resource, params) => {
    return Promise.resolve({ data: params.ids });
  },
};

// React Admin Prototype - Production-ready layout component
const ReactAdminPrototype = ({ children, ...otherProps }) => {
  return <CustomLayout {...otherProps}>{children}</CustomLayout>;
};

export default ReactAdminPrototype;